import React, { useMemo } from "react";
import { motion } from "framer-motion";

/**
 * Animated pixel-block backdrop for the Hero section.
 *
 * Builds two grids of small blocks (black on the left, orange on the right)
 * that fade in one by one — outer columns/rows first, inner columns/rows last
 * — so the colours "box in" toward the centre with a chunky pixel-art feel.
 *
 * The final shape replicates the existing diagonal split:
 *   Black on the left  ⟶ polygon(0 0, 100% 0, 70% 100%, 0% 100%)
 *   Orange on the right ⟶ polygon(30% 0, 100% 0, 100% 100%, 0% 100%)
 *
 * We respect that by sizing each grid to ~55% of the section width and clipping
 * the wrapper with the same polygon used before.
 */

const COLS = 14;
const ROWS = 10;

function buildBlocks(side) {
  // Returns a list of { row, col, delay } objects.
  const blocks = [];
  for (let r = 0; r < ROWS; r++) {
    for (let c = 0; c < COLS; c++) {
      // For LEFT side: outer = small c (far left). For RIGHT side: outer = large c (far right).
      // delay = how far this block is from the OUTER edge (so outer fills first).
      const distFromOuter = side === "left" ? c : COLS - 1 - c;
      // tiny random jitter for a more natural pixel-rain feel
      const jitter = Math.random() * 0.12;
      const delay = distFromOuter * 0.07 + jitter;
      blocks.push({ row: r, col: c, delay });
    }
  }
  return blocks;
}

export default function HeroPixelBg() {
  const leftBlocks = useMemo(() => buildBlocks("left"), []);
  const rightBlocks = useMemo(() => buildBlocks("right"), []);

  const cellStyle = {
    width: `${100 / COLS}%`,
    height: `${100 / ROWS}%`,
  };

  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden="true">
      {/* LEFT — black pixel grid, clipped to the diagonal */}
      <div
        className="absolute left-0 top-0 h-full w-[55%]"
        style={{ clipPath: "polygon(0 0, 100% 0, 70% 100%, 0% 100%)" }}
      >
        <div className="relative h-full w-full">
          {leftBlocks.map((b) => (
            <motion.div
              key={`l-${b.row}-${b.col}`}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, delay: b.delay, ease: "easeOut" }}
              className="absolute bg-brand-black"
              style={{
                left: `${(b.col / COLS) * 100}%`,
                top: `${(b.row / ROWS) * 100}%`,
                ...cellStyle,
              }}
            />
          ))}
        </div>
      </div>

      {/* RIGHT — orange pixel grid, clipped to the diagonal */}
      <div
        className="absolute -right-24 top-0 h-full w-[55%]"
        style={{ clipPath: "polygon(30% 0, 100% 0, 100% 100%, 0% 100%)" }}
      >
        <div className="relative h-full w-full">
          {rightBlocks.map((b) => (
            <motion.div
              key={`r-${b.row}-${b.col}`}
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35, delay: b.delay, ease: "easeOut" }}
              className="absolute bg-brand-orange"
              style={{
                left: `${(b.col / COLS) * 100}%`,
                top: `${(b.row / ROWS) * 100}%`,
                ...cellStyle,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
